import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  calculateStep,
  calculateUnit,
  DateRangeUnitEnum,
  MonitoringDateRange,
} from '../../monitoring/DateRange-Dropdown-util';
import { Card, ColumnLayout, FlexGroup, FlexItem } from '@lithos';
import { TimeSelector } from '@tecton';
import moment from 'moment';
import PrometheusMonitoring from '../../monitoring/PrometheusMonitoring';
import { formatBytesPerSecond } from './utils';

const IngestServerGroupCharts: FC<{ monitoringDateRange: MonitoringDateRange; id: string }> = ({
  monitoringDateRange,
  id,
}) => {
  return (
    <ColumnLayout>
      <Card title="Request Rate">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Request Rate',
              query: `sum by (workspace) (rate(stream_ingest_v2_http_requests_total{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}',tecton_cluster=~'.*'}[5m]))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Requests Per Second'}
          yTicksCallback={undefined}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + ' requests/s';
          }}
        />
      </Card>
      <Card title="Response Percentage By Code">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Response Percentage',
              query: `(sum(rate(stream_ingest_v2_http_responses_total{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m])) by (status_code, tecton_cluster) / ignoring(status_code) group_left sum by (tecton_cluster) (rate(stream_ingest_v2_http_responses_total{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m]))) * 100`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'% by Status Code'}
          yTicksCallback={(value: any) => {
            return value + '%';
          }}
          yGrace={''}
          getMetricLabelCallback={(arg1: any, label?: string | undefined) => {
            return arg1.metric.status_code?.toString() ?? '';
          }}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y.toFixed(2) + '%';
          }}
        />
      </Card>
      <Card title="Request Latency">
        <></>
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'p50',
              query: `histogram_quantile(0.5, sum(rate(stream_ingest_v2_http_request_duration_ms_bucket{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p90',
              query: `histogram_quantile(0.9, sum(rate(stream_ingest_v2_http_request_duration_ms_bucket{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p95',
              query: `histogram_quantile(0.95, sum(rate(stream_ingest_v2_http_request_duration_ms_bucket{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p99',
              query: `histogram_quantile(0.99, sum(rate(stream_ingest_v2_http_request_duration_ms_bucket{ aws_region=~'(us-east-1|us-west-2)', ingest_server_group=~'tecton-isg-${id}', method='POST', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Latency (ms)'}
          yTicksCallback={(value: any) => {
            return value + 'ms';
          }}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y.toLocaleString() + 'ms';
          }}
        />
      </Card>
      <Card title="Server Group Capacity">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Desired',
              query: `aws_autoscaling_group_desired_capacity_average{
  auto_scaling_group_name=~"tecton-isg-${id}",
  tecton_cluster=~'.*'
}`,
            },
            {
              label: 'Average',
              query: `aws_autoscaling_group_min_size_average{
  auto_scaling_group_name=~"tecton-isg-${id}",
  tecton_cluster=~'.*'
}
`,
            },
            {
              label: 'Max',
              query: `aws_autoscaling_group_max_size_average{
  auto_scaling_group_name=~"tecton-isg-${id}",
  tecton_cluster=~'.*'
}`,
            },
            {
              label: 'Current',
              query: `aws_autoscaling_group_in_service_instances_average{
  auto_scaling_group_name=~"tecton-isg-${id}",
  tecton_cluster=~'.*'
}`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Node Count'}
          yTicksCallback={undefined}
          yGrace={''}
          toolTipLabelCallback={undefined}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
        />
      </Card>
      <Card title="CPU Utilization">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Min',
              query: `100 - (min(irate(node_cpu_seconds_total{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group",mode="idle"}[5m])) * 100)`,
            },
            {
              label: 'Average',
              query: `100 - (avg(irate(node_cpu_seconds_total{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group",mode="idle"}[5m])) * 100)`,
            },
            {
              label: 'Max',
              query: `100 - (max(irate(node_cpu_seconds_total{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group",mode="idle"}[5m])) * 100)`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Utilization (%)'}
          yTicksCallback={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100 + '%';
          }}
          yGrace={''}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + '%';
          }}
        />
      </Card>
      <Card title="Memory Utilization">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Min',
              query: `min((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"})) * 100)`,
            },
            {
              label: 'Avg',
              query: `avg((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"})) * 100)`,
            },
            {
              label: 'Max',
              query: `max((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",ingest_server_group=~"tecton-isg-${id}",job="ingest_server_group"})) * 100)`,
            },
          ]}
          xAxisLabel={'Time'}
          yAxisLabel={'Utilization (%)'}
          yTicksCallback={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100 + '%';
          }}
          yGrace={''}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + '%';
          }}
        />
      </Card>

      <Card title="Network Inbound / Outbound Throughput">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Receive',
              query: `sum by (ingest_server_group) (
  rate(node_network_receive_bytes_total{
    job="ingest_server_group",
    tecton_cluster=~".*",
    ingest_server_group=~"tecton-isg-${id}",
    device!~"^lo|docker|veth|br|virbr|cni|flannel"
  }[5m])
)
`,
            },
            {
              label: 'Transmit',
              query: `sum by (ingest_server_group) (
  rate(node_network_transmit_bytes_total{
    job="ingest_server_group",
    tecton_cluster=~".*",
    ingest_server_group=~"tecton-isg-${id}",
    device!~"^lo|docker|veth|br|virbr|cni|flannel"
  }[5m])
)
`,
            },
          ]}
          xAxisLabel={'Time'}
          yAxisLabel={'Throughput'}
          yTicksCallback={(value: any) => {
            return formatBytesPerSecond(value);
          }}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + formatBytesPerSecond(value.raw.y);
          }}
        />
      </Card>
      <Card title="Active Stream Sources">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Active Stream Sources',
              query: `count by (tecton_cluster, ingest_server_group, stream_source_name) (sum by (tecton_cluster, ingest_server_group, stream_source_name) (tecton_ingest_source_requests_total{tecton_cluster=~".*", ingest_server_group=~"tecton-isg-${id}"}))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Active Stream Sources'}
          yTicksCallback={undefined}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y.toFixed(0);
          }}
        />
      </Card>
    </ColumnLayout>
  );
};

const DEFAULT_TIME_RANGE = {
  startTime: () => moment().subtract(1, 'hour').toDate().getTime(),
  endTime: () => moment().toDate().getTime(),
  step: 600,
  requestId: new Date().getTime().toString(),
  unit: DateRangeUnitEnum.minute,
};

const IngestServerGroupMonitoring = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<MonitoringDateRange>(DEFAULT_TIME_RANGE);
  const { id } = useParams();
  return (
    <FlexGroup direction="column" gapSize="4">
      <FlexItem grow="1" shrink="0">
        <TimeSelector
          onSelection={(start, end) => {
            setSelectedTimeRange({
              startTime: () => start.valueOf(),
              endTime: () => end.valueOf(),
              step: calculateStep(moment(start), moment(end)),
              requestId: new Date().getTime().toString(),
              unit: calculateUnit(moment(start), moment(end)),
            });
          }}
        />
      </FlexItem>
      <FlexItem>
        <IngestServerGroupCharts monitoringDateRange={selectedTimeRange} id={id ?? ''} />
      </FlexItem>
    </FlexGroup>
  );
};

export default IngestServerGroupMonitoring;
